import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL } from '@sorare/core/src/atoms/typography';
import { Countdown } from '@sorare/core/src/components/Countdown';
import { LeagueAvatar } from '@sorare/core/src/components/league/LeagueAvatar';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import type { LeagueLaunch } from '@sorare/marketplace/src/lib/newSeason';
import {
  getLeagueAvatar,
  getLeagueBackgroundUrl,
} from '@sorare/marketplace/src/lib/newSeason';

import { LeagueRewards } from '#src/components/new-season/LeagueRewards/index.tsx';

import type {
  MainLeagueLaunch_competition,
  MainLeagueLaunch_so5Competition,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ center: true, gap: 4 })`
  --launch-bg: var(--c-nd-50);
  position: relative;
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-100);
  width: 100%;
  background:
    linear-gradient(
      rgba(var(--c-rgb-black), 0.4),
      rgba(var(--c-rgb-black), 0.4)
    ),
    var(--launch-bg) center / cover no-repeat;
  overflow: hidden;
  padding: calc(var(--unit) * 8) var(--double-unit) var(--double-unit);

  @container ${tabletAndAbove} {
    border-radius: var(--triple-unit);
  }
`;

const LeagueLogoContainer = styled(Vertical)`
  max-width: 80%;
  > * {
    width: auto;
    height: 90px;
  }
`;

const FullWidth = styled(Horizontal)`
  width: 100%;
`;

type Props = {
  league: LeagueLaunch<
    MainLeagueLaunch_competition | undefined,
    MainLeagueLaunch_so5Competition | undefined
  >;
};

export const MainLeagueLaunch = ({ league }: Props) => {
  const backgroundPictureUrl = getLeagueBackgroundUrl(league);
  const leagueAvatar = getLeagueAvatar(league);
  const leagueNode =
    (!!leagueAvatar?.avatarUrl && (
      <LeagueLogoContainer>
        <LeagueAvatar league={leagueAvatar} />
      </LeagueLogoContainer>
    )) ||
    league?.iconNode;

  return (
    <Root
      style={
        {
          '--launch-bg': `url(${backgroundPictureUrl})`,
        } as CSSProperties
      }
    >
      {leagueNode}
      <Countdown
        greyOutZeros
        date={league.startDate}
        leftPad
        Label={BrandL}
        gap={2.5}
      />
      <FullWidth>
        <LeagueRewards league={league} />
      </FullWidth>
    </Root>
  );
};

MainLeagueLaunch.fragments = {
  competition: gql`
    fragment MainLeagueLaunch_competition on Competition {
      slug
      name
      avatarUrl: pictureUrl
      backgroundPictureUrl
      ...getLeagueAvatar_competition
      ...getLeagueBackgroundUrl_competition
    }
    ${getLeagueAvatar.fragments.competition}
    ${getLeagueBackgroundUrl.fragments.competition}
  ` as TypedDocumentNode<MainLeagueLaunch_competition>,

  so5Competition: gql`
    fragment MainLeagueLaunch_so5Competition on So5Competition {
      slug
      ...getLeagueAvatar_so5Competition
      ...getLeagueBackgroundUrl_so5Competition
    }
    ${getLeagueAvatar.fragments.so5Competition}
    ${getLeagueBackgroundUrl.fragments.so5Competition}
  ` as TypedDocumentNode<MainLeagueLaunch_so5Competition>,
};
